import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchAuthBlob } from '@/shared/api'
import { downloadAuthFile } from '@/shared/utils/download'
import { useGenerationProgress } from '../hooks/useGenerationProgress'
import { useGenerationFormStore } from '../store'
import { followUpGeneration } from '../api'
import type { FollowUpRequest } from '../types'
import { IconCheck, IconDownload, IconSpinner, IconX } from '@/app/components/icons'

interface ProgressPanelProps {
  readonly generationId: string
}

export function ProgressPanel({ generationId }: ProgressPanelProps) {
  const { t } = useTranslation()
  const setIsGenerating = useGenerationFormStore((s) => s.setIsGenerating)
  const setActiveGenerationId = useGenerationFormStore((s) => s.setActiveGenerationId)

  const {
    progressPercent,
    previewUrl,
    resultPath,
    errorMessage,
    isComplete,
    isFailed,
    isCancelled,
    cancel,
  } = useGenerationProgress(generationId)

  const isTerminal = isComplete || isFailed || isCancelled

  // Fetch result image via authenticated request to get a blob URL
  const [resultBlobUrl, setResultBlobUrl] = useState<string | null>(null)
  const blobUrlRef = useRef<string | null>(null)

  useEffect(() => {
    if (!resultPath) return

    let cancelled = false
    const path = `/api/history/${generationId}/image`
    void fetchAuthBlob(path).then((url) => {
      if (cancelled || !url) return
      blobUrlRef.current = url
      setResultBlobUrl(url)
    })

    return () => {
      cancelled = true
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [resultPath, generationId])

  // Auto-reset isGenerating when terminal state reached
  useEffect(() => {
    if (isTerminal) {
      setIsGenerating(false)
    }
  }, [isTerminal, setIsGenerating])

  const handleDownload = useCallback(() => {
    downloadAuthFile(`/api/history/${generationId}/image`, `generation-${generationId}.png`)
  }, [generationId])

  const handleNewGeneration = () => {
    setIsGenerating(false)
    setActiveGenerationId(null)
  }

  // Follow-up state
  const [followUpType, setFollowUpType] = useState<'upscale' | 'hires_fix' | null>(null)
  const [followUpLoading, setFollowUpLoading] = useState(false)
  const [followUpError, setFollowUpError] = useState<string | null>(null)

  const handleFollowUp = useCallback(async (type: 'upscale' | 'hires_fix') => {
    setFollowUpType(type)
    setFollowUpLoading(true)
    setFollowUpError(null)

    const request: FollowUpRequest = {
      follow_up_type: type,
      upscale_factor: 2.0,
      seed: -1,
      steps: 20,
      cfg_scale: 7.0,
    }

    try {
      const result = await followUpGeneration(generationId, request)
      if (result.ok) {
        // Switch to the new follow-up generation
        setActiveGenerationId(result.value.generation_id)
        setIsGenerating(true)
      } else {
        setFollowUpError(result.error.message ?? 'Follow-up generation failed')
      }
    } catch {
      setFollowUpError('Failed to start follow-up generation')
    } finally {
      setFollowUpLoading(false)
    }
  }, [generationId, setActiveGenerationId, setIsGenerating])

  const percentDisplay = Math.round(progressPercent * 100)

  const imageUrl = resultBlobUrl ?? previewUrl ?? null

  return (
    <div className="flex flex-1 flex-col items-center gap-6 animate-fade-in">
      {/* Preview / Result Image */}
      <div
        className="relative flex aspect-square w-full max-w-sm items-center justify-center overflow-hidden rounded-2xl border shadow-xl"
        style={{
          borderColor: 'color-mix(in srgb, var(--tg-theme-hint-color) 10%, transparent)',
          backgroundColor: 'var(--tg-theme-secondary-bg-color)',
          boxShadow: '0 8px 32px color-mix(in srgb, var(--tg-theme-hint-color) 12%, transparent)',
        }}
      >
        {imageUrl !== null ? (
          <img
            src={imageUrl}
            alt="Generation result"
            className="h-full w-full object-contain animate-fade-in"
          />
        ) : (
          <div className="flex flex-col items-center gap-4 animate-pulse-soft">
            <div className="relative flex h-16 w-16 items-center justify-center">
              <div
                className="absolute inset-0 animate-spin rounded-full border-2"
                style={{
                  borderColor: 'color-mix(in srgb, var(--tg-theme-button-color) 20%, transparent)',
                  borderTopColor: 'var(--tg-theme-button-color)',
                }}
              />
              <IconSpinner size={24} className="text-tg-button/60" />
            </div>
            <span className="text-sm font-medium text-tg-hint">
              {t('generate.generating')}
            </span>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      {!isTerminal && (
        <div className="w-full max-w-sm">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-tg-hint">
              {t('generate.progress')}
            </span>
            <span
              className="text-xs font-bold tabular-nums"
              style={{ color: 'var(--tg-theme-button-color)' }}
            >
              {String(percentDisplay)}%
            </span>
          </div>
          <div
            className="h-3 w-full overflow-hidden rounded-full shadow-inner"
            style={{
              backgroundColor: 'var(--tg-theme-secondary-bg-color)',
              boxShadow: 'inset 0 1px 3px color-mix(in srgb, var(--tg-theme-hint-color) 15%, transparent)',
            }}
          >
            <div
              className="h-full rounded-full transition-all duration-500 ease-out progress-glow"
              style={{
                width: `${String(percentDisplay)}%`,
                background: 'linear-gradient(90deg, var(--tg-theme-button-color), color-mix(in srgb, var(--tg-theme-button-color), white 20%))',
              }}
            />
          </div>
        </div>
      )}

      {/* Status Messages */}
      {isComplete && (
        <div className="flex items-center gap-2 text-center animate-fade-in">
          <div
            className="flex h-7 w-7 items-center justify-center rounded-full"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--tg-theme-button-color) 20%, transparent)',
            }}
          >
            <IconCheck size={16} className="text-tg-button" />
          </div>
          <span
            className="text-sm font-semibold"
            style={{ color: 'var(--tg-theme-button-color)' }}
          >
            {t('generate.completed')}
          </span>
        </div>
      )}

      {isFailed && (
        <div className="text-center animate-fade-in">
          <div className="mb-1 flex items-center justify-center gap-2">
            <div
              className="flex h-7 w-7 items-center justify-center rounded-full"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--tg-theme-destructive-text-color, #ef4444) 15%, transparent)',
              }}
            >
              <IconX size={14} className="text-tg-destructive" />
            </div>
            <span className="text-sm font-semibold text-tg-destructive">
              {t('generate.failed')}
            </span>
          </div>
          {errorMessage !== null && (
            <p className="mt-1 max-w-xs text-xs text-tg-hint">
              {errorMessage}
            </p>
          )}
        </div>
      )}

      {isCancelled && (
        <div className="text-center animate-fade-in">
          <span className="text-sm font-medium text-tg-hint">
            {t('generate.cancelled')}
          </span>
        </div>
      )}

      {/* Follow-up Error */}
      {followUpError !== null && (
        <div className="text-center text-xs text-tg-destructive animate-fade-in">
          {followUpError}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex w-full max-w-sm gap-3">
        {!isTerminal ? (
          <button
            type="button"
            onClick={cancel}
            className="w-full rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-150 active:scale-[0.98]"
            style={{
              border: '1px solid color-mix(in srgb, var(--tg-theme-destructive-text-color, #ef4444) 30%, transparent)',
              color: 'var(--tg-theme-destructive-text-color, #ef4444)',
            }}
          >
            {t('generate.cancel')}
          </button>
        ) : (
          <>
            {isComplete && (
              <div className="flex w-full flex-col gap-2">
                {/* Primary actions */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-center text-sm font-semibold transition-all duration-150 active:scale-[0.98] text-tg-button-text"
                    style={{
                      background: 'linear-gradient(135deg, var(--tg-theme-button-color), color-mix(in srgb, var(--tg-theme-button-color), black 15%))',
                      boxShadow: '0 2px 8px color-mix(in srgb, var(--tg-theme-button-color) 30%, transparent)',
                    }}
                  >
                    <IconDownload size={16} />
                    {t('generate.downloadImage')}
                  </button>
                  <button
                    type="button"
                    onClick={handleNewGeneration}
                    className="flex-1 rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-150 active:scale-[0.98] text-tg-text hover:bg-tg-secondary"
                    style={{
                      border: '1px solid color-mix(in srgb, var(--tg-theme-hint-color) 20%, transparent)',
                    }}
                  >
                    {t('generate.newGeneration')}
                  </button>
                </div>

                {/* Follow-up actions */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { void handleFollowUp('upscale') }}
                    disabled={followUpLoading}
                    className="flex-1 rounded-xl px-4 py-2.5 text-xs font-semibold transition-all duration-150 active:scale-[0.98] text-tg-button-text disabled:opacity-50"
                    style={{
                      background: 'color-mix(in srgb, var(--tg-theme-button-color) 15%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--tg-theme-button-color) 30%, transparent)',
                    }}
                  >
                    {followUpLoading && followUpType === 'upscale'
                      ? t('generate.processing')
                      : t('generate.upscale')}
                  </button>
                  <button
                    type="button"
                    onClick={() => { void handleFollowUp('hires_fix') }}
                    disabled={followUpLoading}
                    className="flex-1 rounded-xl px-4 py-2.5 text-xs font-semibold transition-all duration-150 active:scale-[0.98] text-tg-button-text disabled:opacity-50"
                    style={{
                      background: 'color-mix(in srgb, var(--tg-theme-button-color) 15%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--tg-theme-button-color) 30%, transparent)',
                    }}
                  >
                    {followUpLoading && followUpType === 'hires_fix'
                      ? t('generate.processing')
                      : t('generate.hiresFix')}
                  </button>
                </div>
              </div>
            )}
            {!isComplete && (
              <button
                type="button"
                onClick={handleNewGeneration}
                className="flex-1 rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-150 active:scale-[0.98] text-tg-text hover:bg-tg-secondary"
                style={{
                  border: '1px solid color-mix(in srgb, var(--tg-theme-hint-color) 20%, transparent)',
                }}
              >
                {t('generate.newGeneration')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
